import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  ShoppingCart, User, LogOut, Shield, Users, Home, Trash2, UserCog, Ban, MapPin,
  Eye, Package, DollarSign, Clock, Truck, CheckCircle, X, Gavel, MailCheck, Send, Edit2, Save, MessageSquareOff, Globe, Flag
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Valid US states for dropdown
const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'PR', name: 'Puerto Rico' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'Washington DC' }
];
const LOGO_URL = "https://customer-assets.emergentagent.com/job_91be65d6-9fee-4eda-abde-3fd83cab62ae/artifacts/kxr3j7eu_IMG_7793.png";

// International email domains (common non-US email providers)
const INTERNATIONAL_EMAIL_DOMAINS = [
  'naver.com', 'daum.net', 'hanmail.net', 'kakao.com', // Korea
  'qq.com', '163.com', '126.com', 'sina.com', 'sohu.com', // China
  'yahoo.co.jp', 'docomo.ne.jp', 'softbank.ne.jp', // Japan
  'mail.ru', 'yandex.ru', // Russia
  'gmx.de', 'web.de', 't-online.de', // Germany
  'orange.fr', 'free.fr', 'sfr.fr', // France
];

// Helper to check if email suggests international user
const isLikelyInternational = (user) => {
  if (user.is_international) return true;
  if (user.country && user.country !== 'US') return true;
  const email = (user.email || '').toLowerCase();
  return INTERNATIONAL_EMAIL_DOMAINS.some(domain => email.endsWith('@' + domain));
};

// Get country hint from email domain
const getCountryFromEmail = (email) => {
  const domain = (email || '').toLowerCase().split('@')[1] || '';
  if (['naver.com', 'daum.net', 'hanmail.net', 'kakao.com'].includes(domain)) return 'KR';
  if (['qq.com', '163.com', '126.com', 'sina.com', 'sohu.com'].includes(domain)) return 'CN';
  if (['yahoo.co.jp', 'docomo.ne.jp', 'softbank.ne.jp'].includes(domain)) return 'JP';
  if (['mail.ru', 'yandex.ru'].includes(domain)) return 'RU';
  if (['gmx.de', 'web.de', 't-online.de'].includes(domain)) return 'DE';
  if (['orange.fr', 'free.fr', 'sfr.fr'].includes(domain)) return 'FR';
  return '?';
};

const RESTRICTION_REASONS = [
  { value: 'chargebacks', label: 'Chargebacks' },
  { value: 'harassment', label: 'Harassment' },
  { value: 'suspected_bot', label: 'Suspected Bot' },
  { value: 'banned_address', label: 'Banned Address' },
  { value: 'other', label: 'Other' }
];

export default function AdminUsers() {
  const { user, logout, getAuthHeader } = useAuth();
  const { itemCount } = useCart();
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showInternationalOnly, setShowInternationalOnly] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, user: null, action: null });
  const [restrictDialog, setRestrictDialog] = useState({ open: false, user: null, reason: '' });
  const [userDetailDialog, setUserDetailDialog] = useState({ open: false, user: null, orders: [], stats: null, bids: [], loading: false });
  
  // Flag user state
  const [flagDialog, setFlagDialog] = useState({ open: false, user: null, reason: '', requireAdminApproval: false, saving: false });
  
  // Edit user state
  const [editUserDialog, setEditUserDialog] = useState({ 
    open: false, 
    user: null, 
    saving: false,
    form: { email: '', full_name: '', country: '', address: '', apt_unit: '', city: '', state: '', zip_code: '', forwarding_verified: false }
  });

  // Countries list for dropdown
  // Comprehensive list of countries supported by forwarding services AND PayPal G&S
  // EXCLUDED: OFAC sanctioned + PayPal restricted countries
  const COUNTRIES = [
    { code: 'US', name: 'United States' },
    // Asia Pacific
    { code: 'KR', name: 'South Korea' },
    { code: 'JP', name: 'Japan' },
    { code: 'CN', name: 'China' },
    { code: 'TW', name: 'Taiwan' },
    { code: 'HK', name: 'Hong Kong' },
    { code: 'SG', name: 'Singapore' },
    { code: 'PH', name: 'Philippines' },
    { code: 'TH', name: 'Thailand' },
    { code: 'MY', name: 'Malaysia' },
    { code: 'ID', name: 'Indonesia' },
    { code: 'VN', name: 'Vietnam' },
    { code: 'IN', name: 'India' },
    { code: 'LK', name: 'Sri Lanka' },
    { code: 'NP', name: 'Nepal' },
    { code: 'KH', name: 'Cambodia' },
    { code: 'MN', name: 'Mongolia' },
    // Oceania
    { code: 'AU', name: 'Australia' },
    { code: 'NZ', name: 'New Zealand' },
    { code: 'FJ', name: 'Fiji' },
    // Europe
    { code: 'GB', name: 'United Kingdom' },
    { code: 'DE', name: 'Germany' },
    { code: 'FR', name: 'France' },
    { code: 'IT', name: 'Italy' },
    { code: 'ES', name: 'Spain' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'BE', name: 'Belgium' },
    { code: 'AT', name: 'Austria' },
    { code: 'CH', name: 'Switzerland' },
    { code: 'PT', name: 'Portugal' },
    { code: 'IE', name: 'Ireland' },
    { code: 'SE', name: 'Sweden' },
    { code: 'NO', name: 'Norway' },
    { code: 'DK', name: 'Denmark' },
    { code: 'FI', name: 'Finland' },
    { code: 'IS', name: 'Iceland' },
    { code: 'PL', name: 'Poland' },
    { code: 'CZ', name: 'Czech Republic' },
    { code: 'SK', name: 'Slovakia' },
    { code: 'HU', name: 'Hungary' },
    { code: 'RO', name: 'Romania' },
    { code: 'BG', name: 'Bulgaria' },
    { code: 'HR', name: 'Croatia' },
    { code: 'SI', name: 'Slovenia' },
    { code: 'RS', name: 'Serbia' },
    { code: 'GR', name: 'Greece' },
    { code: 'CY', name: 'Cyprus' },
    { code: 'LT', name: 'Lithuania' },
    { code: 'LV', name: 'Latvia' },
    { code: 'EE', name: 'Estonia' },
    { code: 'UA', name: 'Ukraine' },
    { code: 'TR', name: 'Turkey' },
    { code: 'GE', name: 'Georgia' },
    // Middle East
    { code: 'AE', name: 'United Arab Emirates' },
    { code: 'SA', name: 'Saudi Arabia' },
    { code: 'IL', name: 'Israel' },
    { code: 'QA', name: 'Qatar' },
    { code: 'KW', name: 'Kuwait' },
    { code: 'BH', name: 'Bahrain' },
    { code: 'OM', name: 'Oman' },
    { code: 'JO', name: 'Jordan' },
    { code: 'EG', name: 'Egypt' },
    // Americas
    { code: 'CA', name: 'Canada' },
    { code: 'MX', name: 'Mexico' },
    { code: 'BR', name: 'Brazil' },
    { code: 'AR', name: 'Argentina' },
    { code: 'CL', name: 'Chile' },
    { code: 'CO', name: 'Colombia' },
    { code: 'PE', name: 'Peru' },
    { code: 'CR', name: 'Costa Rica' },
    { code: 'PA', name: 'Panama' },
    { code: 'DO', name: 'Dominican Republic' },
    { code: 'PR', name: 'Puerto Rico' },
    // Africa
    { code: 'ZA', name: 'South Africa' },
    { code: 'NG', name: 'Nigeria' },
    { code: 'MA', name: 'Morocco' },
    { code: 'KE', name: 'Kenya' },
    { code: 'GH', name: 'Ghana' },
  ];

  const getCountryName = (code) => {
    const country = COUNTRIES.find(c => c.code === code);
    return country ? country.name : code || 'US';
  };

  // Check for search param from URL (e.g., from Top Spenders link)
  useEffect(() => {
    const urlSearch = searchParams.get('search');
    if (urlSearch) {
      setSearchInput(urlSearch);
      setSearchQuery(urlSearch.toLowerCase());
    }
  }, [searchParams]);

  // Auto-open user detail if URL search matches exactly one user
  useEffect(() => {
    const urlSearch = searchParams.get('search');
    if (urlSearch && users.length > 0 && !loading) {
      const searchLower = urlSearch.toLowerCase();
      const matchingUsers = users.filter(u => 
        u.email?.toLowerCase().includes(searchLower) ||
        u.full_name?.toLowerCase().includes(searchLower)
      );
      
      // If exactly one match, auto-open their profile
      if (matchingUsers.length === 1) {
        handleViewUser(matchingUsers[0]);
      }
    }
  }, [users, searchParams, loading]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: getAuthHeader()
      });
      // Sort by newest first (created_at descending)
      const sortedUsers = [...response.data].sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA; // Newest first
      });
      setUsers(sortedUsers);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  // Edit user functions
  const handleEditUser = (targetUser) => {
    setEditUserDialog({
      open: true,
      user: targetUser,
      saving: false,
      form: {
        email: targetUser.email || '',
        full_name: targetUser.full_name || '',
        country: targetUser.country || 'US',
        is_international: targetUser.is_international || false,
        home_country: targetUser.home_country || '',
        address: targetUser.address || '',
        apt_unit: targetUser.apt_unit || '',
        city: targetUser.city || '',
        state: targetUser.state || '',
        zip_code: targetUser.zip_code || '',
        forwarding_verified: targetUser.forwarding_verified || false
      }
    });
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditUserDialog(prev => ({
      ...prev,
      form: { ...prev.form, [name]: value }
    }));
  };

  const handleSaveUserEdit = async () => {
    if (!editUserDialog.user || editUserDialog.saving) return;
    
    setEditUserDialog(prev => ({ ...prev, saving: true }));
    try {
      await axios.put(
        `${API}/admin/users/${editUserDialog.user.id}`,
        editUserDialog.form,
        { headers: getAuthHeader() }
      );
      toast.success('User profile updated');
      setEditUserDialog({ open: false, user: null, saving: false, form: { email: '', full_name: '', country: '', address: '', apt_unit: '', city: '', state: '', zip_code: '' } });
      fetchUsers();
    } catch (error) {
      console.error('Update user error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user');
      setEditUserDialog(prev => ({ ...prev, saving: false }));
    }
  };

  const handleCloseEditDialog = () => {
    if (editUserDialog.saving) return; // Don't close while saving
    setEditUserDialog({ 
      open: false, 
      user: null, 
      saving: false, 
      form: { email: '', full_name: '', country: '', address: '', city: '', state: '', zip_code: '' } 
    });
  };

  // Flag user handlers
  const handleOpenFlagDialog = (user) => {
    setFlagDialog({ open: true, user, reason: '', saving: false });
  };

  const handleFlagUser = async () => {
    if (!flagDialog.user || flagDialog.saving) return;
    
    setFlagDialog(prev => ({ ...prev, saving: true }));
    try {
      const params = new URLSearchParams();
      if (flagDialog.reason) params.append('reason', flagDialog.reason);
      if (flagDialog.requireAdminApproval) params.append('require_admin_approval', 'true');
      
      await axios.post(
        `${API}/admin/users/${flagDialog.user.id}/flag-profile?${params.toString()}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(`User ${flagDialog.user.email} flagged${flagDialog.requireAdminApproval ? ' (admin approval required)' : ''}`);
      setFlagDialog({ open: false, user: null, reason: '', requireAdminApproval: false, saving: false });
      fetchUsers();
    } catch (error) {
      console.error('Flag user error:', error);
      toast.error(error.response?.data?.detail || 'Failed to flag user');
      setFlagDialog(prev => ({ ...prev, saving: false }));
    }
  };

  const handleUnflagUser = async (user) => {
    try {
      await axios.post(
        `${API}/admin/users/${user.id}/unflag-profile`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(`Flag removed from ${user.email}`);
      fetchUsers();
    } catch (error) {
      console.error('Unflag user error:', error);
      toast.error(error.response?.data?.detail || 'Failed to unflag user');
    }
  };

  const fetchUserOrders = async (userId) => {
    setUserDetailDialog(prev => ({ ...prev, loading: true }));
    try {
      const response = await axios.get(`${API}/admin/users/${userId}/orders`, {
        headers: getAuthHeader()
      });
      setUserDetailDialog(prev => ({
        ...prev,
        user: response.data.user,
        orders: response.data.orders,
        stats: response.data.stats,
        bids: response.data.bids || [],
        loading: false
      }));
    } catch (error) {
      toast.error('Failed to load user orders');
      setUserDetailDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const handleViewUser = (targetUser) => {
    setUserDetailDialog({ open: true, user: targetUser, orders: [], stats: null, bids: [], loading: true });
    fetchUserOrders(targetUser.id);
  };

  const [deletingUserId, setDeletingUserId] = useState(null);

  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(`Delete user ${email}? This cannot be undone.`)) return;
    setDeletingUserId(userId);
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: getAuthHeader()
      });
      toast.success('USER DELETED');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleToggleAdmin = async (targetUser) => {
    if (targetUser.is_admin) {
      try {
        await axios.post(`${API}/admin/remove-admin/${targetUser.id}`, {}, {
          headers: getAuthHeader()
        });
        toast.success('ADMIN REMOVED');
        fetchUsers();
      } catch (error) {
        toast.error('Failed to remove admin status');
      }
    } else {
      setConfirmDialog({ open: true, user: targetUser, action: 'promote' });
    }
  };

  const confirmMakeAdmin = async () => {
    if (!confirmDialog.user) return;
    try {
      await axios.post(`${API}/admin/make-admin/${confirmDialog.user.id}`, {}, {
        headers: getAuthHeader()
      });
      toast.success('USER PROMOTED TO ADMIN');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to promote user');
    }
    setConfirmDialog({ open: false, user: null, action: null });
  };

  const handleVerifyUser = async (targetUser) => {
    try {
      await axios.post(`${API}/admin/verify-user/${targetUser.id}`, {}, {
        headers: getAuthHeader()
      });
      toast.success(`${targetUser.email} VERIFIED`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify user');
    }
  };

  const handleVerifyAllUsers = async () => {
    const unverifiedCount = users.filter(u => !u.is_verified).length;
    if (unverifiedCount === 0) {
      toast.info('No unverified users to verify');
      return;
    }
    
    if (!window.confirm(`Verify all ${unverifiedCount} unverified users? They will be able to login immediately.`)) return;
    
    try {
      const response = await axios.post(`${API}/admin/verify-all-users`, {}, {
        headers: getAuthHeader()
      });
      toast.success(`${response.data.verified_count} USERS VERIFIED`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify users');
    }
  };

  const handleResendVerification = async (targetUser) => {
    try {
      const response = await axios.post(`${API}/admin/resend-verification/${targetUser.id}`, {}, {
        headers: getAuthHeader()
      });
      if (response.data.sent) {
        toast.success(`Verification email sent to ${targetUser.email}`);
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send verification email');
    }
  };

  // "SEND TEST TO ME" — confirms Resend + sender-domain + link URL are all
  // healthy end-to-end by dispatching a verification-shaped email to the
  // admin's own address. Doesn't touch the users collection.
  const handleSendTestVerification = async () => {
    const targetEmail = window.prompt(
      "Send test verification email to which address?\n\n(Leave blank to send to your own admin email)",
      user?.email || ""
    );
    if (targetEmail === null) return; // cancelled
    const email = (targetEmail || user?.email || "").trim();
    if (!email) {
      toast.error("No email provided");
      return;
    }
    try {
      const response = await axios.post(
        `${API}/admin/test-verification-email`,
        { email },
        { headers: getAuthHeader() }
      );
      if (response.data?.success) {
        toast.success(`✓ Test verification email sent to ${email}. Check inbox + spam.`);
      } else {
        toast.error(response.data?.error || 'Resend accepted the request but reported no success');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test verification email');
    }
  };

  const handleToggleRestriction = async (targetUser) => {
    if (targetUser.is_restricted) {
      try {
        await axios.post(`${API}/admin/restrict-user/${targetUser.id}`, {}, {
          headers: getAuthHeader()
        });
        toast.success('USER UNRESTRICTED');
        fetchUsers();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to update user');
      }
    } else {
      setRestrictDialog({ open: true, user: targetUser, reason: 'other' });
    }
  };

  const confirmRestriction = async () => {
    if (!restrictDialog.user) return;
    try {
      await axios.post(`${API}/admin/restrict-user/${restrictDialog.user.id}`, 
        { reason: restrictDialog.reason },
        { headers: getAuthHeader() }
      );
      toast.success('USER RESTRICTED');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restrict user');
    }
    setRestrictDialog({ open: false, user: null, reason: '' });
  };

  const handleBanAddress = async (targetUser) => {
    if (!window.confirm(`Ban ${targetUser.full_name}'s address? They won't be able to create new accounts with this address.`)) return;
    try {
      await axios.post(`${API}/admin/ban-user-address/${targetUser.id}`, {}, {
        headers: getAuthHeader()
      });
      toast.success('ADDRESS BANNED & USER RESTRICTED');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to ban address');
    }
  };

  const handleToggleOffers = async (targetUser) => {
    const action = targetUser.offers_restricted ? 'allow' : 'restrict';
    try {
      await axios.post(`${API}/admin/users/${targetUser.id}/toggle-offers`, {}, {
        headers: getAuthHeader()
      });
      toast.success(targetUser.offers_restricted ? 'USER CAN NOW MAKE OFFERS' : 'USER RESTRICTED FROM OFFERS');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle offers');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-500';
      case 'pending_fulfillment': return 'text-purple-500';
      case 'pending_shipment': return 'text-tiffany-500';
      case 'shipped': return 'text-blue-500';
      case 'delivered': return 'text-green-500';
      default: return 'text-zinc-500';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'pending_fulfillment': return <DollarSign className="w-4 h-4" />;
      case 'pending_shipment': return <Package className="w-4 h-4" />;
      case 'shipped': return <Truck className="w-4 h-4" />;
      case 'delivered': return <CheckCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-tiffany-500 font-mono cursor-blink">LOADING USERS</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-zinc-800 bg-[#050505]/90 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/catalog" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="BLK MRKT" className="h-8 w-auto" />
            <Badge className="bg-tiffany-500 text-black rounded-none border-0 hidden sm:inline-flex">ADMIN</Badge>
          </Link>
          
          <nav className="flex items-center gap-2 sm:gap-4">
            <Link to="/catalog" className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
              <Home className="w-5 h-5" />
            </Link>
            <Link to="/cart" className="relative p-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
              <ShoppingCart className="w-5 h-5" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-tiffany-500 text-black text-xs font-bold w-5 h-5 flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </Link>
            <Link to="/dashboard" className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
              <User className="w-5 h-5" />
            </Link>
            <Link to="/admin" className="p-2 text-tiffany-500">
              <Shield className="w-5 h-5" />
            </Link>
            <button onClick={logout} className="p-2 text-zinc-400 hover:text-red-500 transition-colors">
              <LogOut className="w-5 h-5" />
            </button>
          </nav>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 md:px-8 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-zinc-500 hover:text-tiffany-500 font-mono text-sm">
              ← BACK TO ADMIN
            </Link>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <Users className="w-8 h-8 text-tiffany-500" />
            <h1 className="font-sans text-2xl sm:text-3xl text-white tracking-tighter">
              ALL USERS ({users.length})
            </h1>
          </div>

          <div className="flex items-center gap-2 flex-wrap justify-end">
            {/* Send Test Verification Email — end-to-end deliverability check */}
            <Button
              onClick={handleSendTestVerification}
              className="bg-tiffany-500/20 text-tiffany-500 hover:bg-tiffany-500/30 font-mono text-xs tracking-wider rounded-none border border-tiffany-500/50"
              data-testid="send-test-verification-btn"
              title="Send yourself a test verification email to confirm delivery is working"
            >
              <Send className="w-4 h-4 mr-2" />
              SEND TEST TO ME
            </Button>

            {/* Verify All Unverified Users Button */}
            {users.some(u => !u.is_verified) && (
              <Button
                onClick={handleVerifyAllUsers}
                className="bg-yellow-500/20 text-yellow-500 hover:bg-yellow-500/30 font-mono text-xs tracking-wider rounded-none border border-yellow-500/50"
                data-testid="verify-all-users-btn"
              >
                <MailCheck className="w-4 h-4 mr-2" />
                VERIFY ALL ({users.filter(u => !u.is_verified).length})
              </Button>
            )}
          </div>
        </div>

        {/* Search Bar */}
        <form 
          className="mb-6 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setSearchQuery(searchInput);
          }}
        >
          <Input
            placeholder="SEARCH BY NAME OR EMAIL..."
            value={searchInput}
            onChange={(e) => {
              const input = e.target;
              const start = input.selectionStart;
              const end = input.selectionEnd;
              setSearchInput(e.target.value.toUpperCase());
              requestAnimationFrame(() => input.setSelectionRange(start, end));
            }}
            className="bg-zinc-900 border-zinc-800 text-white rounded-none font-mono uppercase flex-1"
            data-testid="user-search-input"
          />
          <Button
            type="submit"
            className="bg-tiffany-500 text-black hover:bg-tiffany-400 rounded-none font-mono px-6"
            data-testid="user-search-button"
          >
            SEARCH
          </Button>
          {searchQuery && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setSearchInput('');
                setSearchQuery('');
              }}
              className="border-zinc-700 text-zinc-400 hover:text-white rounded-none font-mono px-4"
            >
              CLEAR
            </Button>
          )}
        </form>

        {/* International Filter */}
        <div className="mb-4 flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Checkbox
              id="intl-filter"
              checked={showInternationalOnly}
              onCheckedChange={(checked) => setShowInternationalOnly(checked)}
              className="border-purple-500 data-[state=checked]:bg-purple-500 data-[state=checked]:border-purple-500"
              data-testid="intl-filter-checkbox"
            />
            <label 
              htmlFor="intl-filter" 
              className="font-mono text-sm text-purple-400 cursor-pointer flex items-center gap-2"
            >
              <Globe className="w-4 h-4" />
              INTERNATIONAL ONLY
            </label>
          </div>
          {showInternationalOnly && (
            <span className="font-mono text-xs text-zinc-500">
              ({users.filter(u => isLikelyInternational(u)).length} users)
            </span>
          )}
        </div>

        <div className="space-y-3">
          {users
            .filter(u => {
              // International filter
              if (showInternationalOnly && !isLikelyInternational(u)) return false;
              
              // Search filter
              if (!searchQuery) return true;
              const query = searchQuery.toLowerCase();
              return (
                (u.full_name || '').toLowerCase().includes(query) ||
                (u.email || '').toLowerCase().includes(query) ||
                (u.city || '').toLowerCase().includes(query) ||
                (u.state || '').toLowerCase().includes(query)
              );
            })
            .map((u, i) => (
            <div key={u.id || i} className={`border p-4 transition-colors ${u.is_restricted ? 'border-red-500/50 bg-red-500/5' : 'border-zinc-800 hover:border-zinc-700'}`}>
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <p className="font-mono text-base sm:text-lg text-white truncate">{u.full_name || 'No name'}</p>
                    {u.is_admin && (
                      <Badge className="bg-tiffany-500/20 text-tiffany-500 rounded-none border-0 text-xs">
                        ADMIN
                      </Badge>
                    )}
                    {!u.is_verified && (
                      <Badge className="bg-yellow-500/20 text-yellow-500 rounded-none border-0 text-xs">
                        UNVERIFIED
                      </Badge>
                    )}
                    {u.is_restricted && (
                      <Badge className="bg-red-500/20 text-red-500 rounded-none border-0 text-xs">
                        RESTRICTED
                      </Badge>
                    )}
                    {u.offers_restricted && (
                      <Badge className="bg-orange-500/20 text-orange-500 rounded-none border-0 text-xs">
                        NO OFFERS
                      </Badge>
                    )}
                    {u.profile_flagged && (
                      <Badge className={`${u.require_admin_approval ? 'bg-orange-500/20 text-orange-500' : 'bg-red-500/20 text-red-500'} rounded-none border-0 text-xs`}>
                        {u.require_admin_approval ? 'LOCKED' : 'FLAGGED'}
                      </Badge>
                    )}
                    {(u.country && u.country !== 'US') && (
                      <Badge className="bg-purple-500/20 text-purple-500 rounded-none border-0 text-xs">
                        {getCountryName(u.country)}
                      </Badge>
                    )}
                    {u.is_international && u.country === 'US' && (
                      <Badge className="bg-purple-500/20 text-purple-500 rounded-none border-0 text-xs">
                        {u.home_country ? getCountryName(u.home_country) : 'INTL'} (US FWDG)
                      </Badge>
                    )}
                    {!u.country && isLikelyInternational(u) && (
                      <Badge className="bg-purple-500/20 text-purple-500 rounded-none border-0 text-xs">
                        INTL? ({getCountryFromEmail(u.email)})
                      </Badge>
                    )}
                  </div>
                  <p className="font-mono text-sm text-zinc-400 truncate">{u.email}</p>
                  {u.address && (
                    <p className="font-mono text-xs text-zinc-600 mt-1 truncate">
                      {u.city}, {u.state} {u.zip_code}
                    </p>
                  )}
                  {u.restriction_reason && (
                    <p className="font-mono text-xs text-red-400 mt-1">
                      Reason: {u.restriction_reason}
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <p className="font-mono text-xs text-zinc-500">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}
                  </p>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewUser(u)}
                      className="text-zinc-400 hover:text-tiffany-500 h-8 w-8 p-0"
                      title="View user details & orders"
                      data-testid={`view-btn-${u.id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditUser(u)}
                      className="text-zinc-400 hover:text-blue-500 h-8 w-8 p-0"
                      title="Edit user profile"
                      data-testid={`edit-btn-${u.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    {u.email !== user?.email && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleBanAddress(u)}
                          className="text-zinc-400 hover:text-orange-500 h-8 w-8 p-0"
                          title="Ban address"
                          data-testid={`ban-address-btn-${u.id}`}
                        >
                          <MapPin className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleRestriction(u)}
                          className={`h-8 w-8 p-0 ${u.is_restricted ? "text-red-500 hover:text-green-500" : "text-zinc-400 hover:text-red-500"}`}
                          title={u.is_restricted ? "Unrestrict user" : "Restrict user"}
                          data-testid={`restrict-btn-${u.id}`}
                        >
                          <Ban className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleOffers(u)}
                          className={`h-8 w-8 p-0 ${u.offers_restricted ? "text-orange-500 hover:text-green-500" : "text-zinc-400 hover:text-orange-500"}`}
                          title={u.offers_restricted ? "Allow offers" : "Restrict offers"}
                          data-testid={`offers-btn-${u.id}`}
                        >
                          <MessageSquareOff className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    {/* Flag/Unflag button */}
                    {u.profile_flagged ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleUnflagUser(u)}
                        className="text-red-500 hover:text-green-500 h-8 w-8 p-0"
                        title="Remove flag"
                        data-testid={`unflag-btn-${u.id}`}
                      >
                        <Flag className="w-4 h-4 fill-current" />
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenFlagDialog(u)}
                        className="text-zinc-400 hover:text-red-500 h-8 w-8 p-0"
                        title="Flag user"
                        data-testid={`flag-btn-${u.id}`}
                      >
                        <Flag className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleAdmin(u)}
                      className={`h-8 w-8 p-0 ${u.is_admin ? "text-tiffany-500 hover:text-yellow-500" : "text-zinc-400 hover:text-tiffany-500"}`}
                      title={u.is_admin ? "Remove admin" : "Make admin"}
                      data-testid={`admin-btn-${u.id}`}
                    >
                      <UserCog className="w-4 h-4" />
                    </Button>
                    {!u.is_verified && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleVerifyUser(u)}
                        className="text-yellow-500 hover:text-green-500 h-8 w-8 p-0"
                        title="Manually verify email"
                        data-testid={`verify-btn-${u.id}`}
                      >
                        <MailCheck className="w-4 h-4" />
                      </Button>
                    )}
                    {!u.is_verified && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleResendVerification(u)}
                        className="text-blue-500 hover:text-blue-400 h-8 w-8 p-0"
                        title="Resend verification email"
                        data-testid={`resend-btn-${u.id}`}
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    )}
                    {u.email !== user?.email && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteUser(u.id, u.email)}
                        className="text-zinc-400 hover:text-red-500 h-8 w-8 p-0"
                        title="Delete user"
                        data-testid={`delete-btn-${u.id}`}
                        disabled={deletingUserId === u.id}
                      >
                        {deletingUserId === u.id ? (
                          <div className="w-4 h-4 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* User Detail Dialog with Orders */}
        <Dialog open={userDetailDialog.open} onOpenChange={(open) => !open && setUserDetailDialog({ open: false, user: null, orders: [], stats: null, loading: false })}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white font-sans flex items-center gap-2">
                <User className="w-5 h-5 text-tiffany-500" />
                {userDetailDialog.user?.full_name || 'User Details'}
              </DialogTitle>
              <DialogDescription className="text-zinc-400 font-mono">
                {userDetailDialog.user?.email}
              </DialogDescription>
            </DialogHeader>

            {userDetailDialog.loading ? (
              <div className="py-8 text-center">
                <div className="text-tiffany-500 font-mono">LOADING...</div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* User Info */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-zinc-800/50 border border-zinc-700">
                  <div>
                    <p className="font-mono text-xs text-zinc-500">ADDRESS</p>
                    <p className="font-mono text-sm text-white" data-testid="user-detail-address">
                      {userDetailDialog.user?.address || 'N/A'}
                      {userDetailDialog.user?.apt_unit ? `, ${userDetailDialog.user.apt_unit}` : ''}
                    </p>
                    <p className="font-mono text-sm text-zinc-400">
                      {userDetailDialog.user?.city}, {userDetailDialog.user?.state} {userDetailDialog.user?.zip_code}
                    </p>
                  </div>
                  <div>
                    <p className="font-mono text-xs text-zinc-500">MEMBER SINCE</p>
                    <p className="font-mono text-sm text-white">
                      {userDetailDialog.user?.created_at ? new Date(userDetailDialog.user.created_at).toLocaleDateString() : 'N/A'}
                    </p>
                    {userDetailDialog.user?.is_restricted && (
                      <p className="font-mono text-xs text-red-500 mt-1">
                        RESTRICTED: {userDetailDialog.user?.restriction_reason || 'No reason'}
                      </p>
                    )}
                  </div>
                </div>

                {/* Order Stats */}
                {userDetailDialog.stats && (
                  <div>
                    <h3 className="font-sans text-lg text-white mb-3">ORDER STATS</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="p-3 bg-yellow-500/10 border border-yellow-500/30">
                        <div className="flex items-center gap-2 mb-1">
                          <Clock className="w-4 h-4 text-yellow-500" />
                          <span className="font-mono text-xs text-yellow-500">PENDING</span>
                        </div>
                        <p className="font-mono text-xl text-white">{userDetailDialog.stats.pending}</p>
                        <p className="font-mono text-xs text-zinc-500">${userDetailDialog.stats.pending_total?.toFixed(2)}</p>
                      </div>
                      <div className="p-3 bg-blue-500/10 border border-blue-500/30">
                        <div className="flex items-center gap-2 mb-1">
                          <Package className="w-4 h-4 text-blue-500" />
                          <span className="font-mono text-xs text-blue-500">TO SHIP</span>
                        </div>
                        <p className="font-mono text-xl text-white">{userDetailDialog.stats.pending_shipment}</p>
                      </div>
                      <div className="p-3 bg-purple-500/10 border border-purple-500/30">
                        <div className="flex items-center gap-2 mb-1">
                          <Truck className="w-4 h-4 text-purple-500" />
                          <span className="font-mono text-xs text-purple-500">SHIPPED</span>
                        </div>
                        <p className="font-mono text-xl text-white">{userDetailDialog.stats.shipped}</p>
                      </div>
                      <div className="p-3 bg-green-500/10 border border-green-500/30">
                        <div className="flex items-center gap-2 mb-1">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span className="font-mono text-xs text-green-500">DELIVERED</span>
                        </div>
                        <p className="font-mono text-xl text-white">{userDetailDialog.stats.delivered}</p>
                      </div>
                    </div>
                    <div className="mt-3 p-3 bg-tiffany-500/10 border border-tiffany-500/30">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-tiffany-500" />
                          <span className="font-mono text-sm text-tiffany-500">TOTAL SPENT (Confirmed)</span>
                        </div>
                        <span className="font-mono text-xl text-white">${userDetailDialog.stats.total_spent?.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Orders List */}
                <div>
                  <h3 className="font-sans text-lg text-white mb-3">
                    ORDER HISTORY ({userDetailDialog.stats?.total_orders || 0})
                  </h3>
                  {userDetailDialog.orders.length === 0 ? (
                    <p className="font-mono text-sm text-zinc-500 text-center py-4">No orders yet</p>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {userDetailDialog.orders.map((order) => (
                        <div key={order.id} className="p-3 bg-zinc-800 border border-zinc-700">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-mono text-sm text-white">{order.order_id || order.id}</span>
                            <div className={`flex items-center gap-1 ${getStatusColor(order.status)}`}>
                              {getStatusIcon(order.status)}
                              <span className="font-mono text-xs uppercase">{order.status?.replace('_', ' ')}</span>
                            </div>
                          </div>
                          <div className="flex items-center justify-between text-xs">
                            <span className="font-mono text-zinc-500">
                              {new Date(order.created_at).toLocaleDateString()}
                            </span>
                            <span className="font-mono text-white">${order.total?.toFixed(2)}</span>
                          </div>
                          <div className="mt-2 text-xs text-zinc-500">
                            {order.items?.map((item, i) => (
                              <span key={i} className="mr-2">{item.name} x{item.quantity}</span>
                            ))}
                          </div>
                          {order.tracking_number && (
                            <p className="font-mono text-xs text-tiffany-500 mt-1">
                              Tracking: {order.tracking_number}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Active Bids */}
                {userDetailDialog.bids && userDetailDialog.bids.length > 0 && (
                  <div>
                    <h3 className="font-sans text-lg text-white mb-3">
                      ACTIVE BIDS ({userDetailDialog.bids.filter(b => b.is_active).length})
                    </h3>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {userDetailDialog.bids.filter(b => b.is_active).map((bid) => (
                        <div 
                          key={bid.id} 
                          className={`p-3 border ${bid.is_winning ? 'bg-tiffany-500/10 border-tiffany-500/30' : 'bg-zinc-800 border-zinc-700'}`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-mono text-sm text-white">{bid.product_name}</span>
                            {bid.is_winning ? (
                              <span className="font-mono text-xs text-tiffany-500 bg-tiffany-500/20 px-2 py-1">WINNING</span>
                            ) : (
                              <span className="font-mono text-xs text-red-500 bg-red-500/20 px-2 py-1">OUTBID</span>
                            )}
                          </div>
                          <div className="flex items-center justify-between text-xs">
                            <span className="font-mono text-zinc-500">
                              Their bid: ${bid.amount?.toFixed(2)}
                            </span>
                            <span className="font-mono text-zinc-400">
                              Current high: ${bid.current_high_bid?.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setUserDetailDialog({ open: false, user: null, orders: [], stats: null, loading: false })}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                CLOSE
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Admin Confirmation Dialog */}
        <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, user: null, action: null })}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white font-sans">Confirm Admin Promotion</DialogTitle>
              <DialogDescription className="text-zinc-400 font-mono">
                Are you sure you want to make <span className="text-tiffany-500">{confirmDialog.user?.email}</span> an Admin?
                <br /><br />
                Admins have full access to manage products, orders, and other users.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 flex-col sm:flex-row">
              <Button
                variant="outline"
                onClick={() => setConfirmDialog({ open: false, user: null, action: null })}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 w-full sm:w-auto"
              >
                CANCEL
              </Button>
              <Button
                onClick={confirmMakeAdmin}
                className="bg-tiffany-500 text-black hover:bg-tiffany-400 w-full sm:w-auto"
                data-testid="confirm-admin-btn"
              >
                YES, MAKE ADMIN
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Restriction Dialog with Reason */}
        <Dialog open={restrictDialog.open} onOpenChange={(open) => !open && setRestrictDialog({ open: false, user: null, reason: '' })}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white font-sans">Restrict User</DialogTitle>
              <DialogDescription className="text-zinc-400 font-mono">
                Restricting <span className="text-red-500">{restrictDialog.user?.email}</span> will prevent them from adding items to cart or placing bids.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <label className="block font-mono text-sm text-zinc-500 mb-2">
                REASON FOR RESTRICTION
              </label>
              <Select value={restrictDialog.reason} onValueChange={(value) => setRestrictDialog(prev => ({ ...prev, reason: value }))}>
                <SelectTrigger className="w-full bg-zinc-800 border-zinc-700 text-white rounded-none">
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700 rounded-none">
                  {RESTRICTION_REASONS.map((reason) => (
                    <SelectItem key={reason.value} value={reason.value} className="text-white font-mono">
                      {reason.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="gap-2 flex-col sm:flex-row">
              <Button
                variant="outline"
                onClick={() => setRestrictDialog({ open: false, user: null, reason: '' })}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 w-full sm:w-auto"
              >
                CANCEL
              </Button>
              <Button
                onClick={confirmRestriction}
                className="bg-red-500 text-white hover:bg-red-400 w-full sm:w-auto"
                data-testid="confirm-restrict-btn"
              >
                RESTRICT USER
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog 
          open={editUserDialog.open} 
          onOpenChange={(open) => {
            if (!open && !editUserDialog.saving) {
              handleCloseEditDialog();
            }
          }}
        >
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white font-sans">EDIT USER</DialogTitle>
              <DialogDescription className="text-zinc-400 font-mono text-sm">
                {editUserDialog.user?.email}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Email - full width */}
              <div>
                <label className="block font-mono text-xs text-zinc-500 mb-1">EMAIL</label>
                <Input
                  name="email"
                  type="email"
                  value={editUserDialog.form.email || ''}
                  onChange={handleEditFormChange}
                  disabled={editUserDialog.saving}
                  className="h-9 w-full bg-zinc-800 border-zinc-700 text-white font-mono text-sm rounded-none uppercase"
                  data-testid="admin-edit-email"
                />
              </div>
              
              {/* Full Name - full width on its own line */}
              <div>
                <label className="block font-mono text-xs text-zinc-500 mb-1">FULL NAME</label>
                <Input
                  name="full_name"
                  value={editUserDialog.form.full_name || ''}
                  onChange={handleEditFormChange}
                  disabled={editUserDialog.saving}
                  className="h-9 w-full bg-zinc-800 border-zinc-700 text-white font-mono text-sm rounded-none uppercase"
                  data-testid="admin-edit-name"
                />
              </div>
              
              <div>
                <label className="block font-mono text-xs text-zinc-500 mb-1">COUNTRY</label>
                <Select
                  value={editUserDialog.form.country || undefined}
                  onValueChange={(value) => handleEditFormChange({ target: { name: 'country', value } })}
                  disabled={editUserDialog.saving}
                >
                  <SelectTrigger className="h-9 w-full bg-zinc-800 border-zinc-700 text-white font-mono text-sm rounded-none focus:border-tiffany-500 uppercase" data-testid="admin-edit-country">
                    <SelectValue placeholder="SELECT COUNTRY" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700 text-white font-mono max-h-60">
                    {COUNTRIES.map(country => (
                      <SelectItem key={country.code} value={country.code} className="text-white hover:bg-zinc-700 focus:bg-zinc-700 focus:text-white cursor-pointer uppercase">
                        {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editUserDialog.form.country && editUserDialog.form.country !== 'US' && (
                  <p className="font-mono text-[10px] text-purple-400 mt-1">INTERNATIONAL CUSTOMER</p>
                )}
              </div>
              
              <div>
                <label className="block font-mono text-xs text-zinc-500 mb-1">ADDRESS</label>
                <Input
                  name="address"
                  value={editUserDialog.form.address || ''}
                  onChange={handleEditFormChange}
                  disabled={editUserDialog.saving}
                  className="h-9 w-full bg-zinc-800 border-zinc-700 text-white font-mono text-sm rounded-none uppercase"
                  data-testid="admin-edit-address"
                />
              </div>
              
              <div>
                <label className="block font-mono text-xs text-zinc-500 mb-1">APT / UNIT / SUITE</label>
                <Input
                  name="apt_unit"
                  value={editUserDialog.form.apt_unit || ''}
                  onChange={handleEditFormChange}
                  disabled={editUserDialog.saving}
                  placeholder="OPTIONAL"
                  className="h-9 w-full bg-zinc-800 border-zinc-700 text-white font-mono text-sm rounded-none placeholder:text-zinc-600 uppercase"
                  data-testid="admin-edit-apt-unit"
                />
              </div>
              
              {/* City on its own row */}
              <div>
                <label className="block font-mono text-xs text-zinc-500 mb-1">CITY</label>
                <Input
                  name="city"
                  value={editUserDialog.form.city || ''}
                  onChange={handleEditFormChange}
                  disabled={editUserDialog.saving}
                  className="h-9 w-full bg-zinc-800 border-zinc-700 text-white font-mono text-sm rounded-none uppercase"
                  data-testid="admin-edit-city"
                />
              </div>
              
              {/* State and ZIP on same row */}
              <div className="grid grid-cols-12 gap-3">
                <div className="col-span-8">
                  <label className="block font-mono text-xs text-zinc-500 mb-1">STATE</label>
                  <Select
                    value={editUserDialog.form.state?.toUpperCase() || undefined}
                    onValueChange={(value) => {
                      setEditUserDialog(prev => ({
                        ...prev,
                        form: { ...prev.form, state: value }
                      }));
                    }}
                    disabled={editUserDialog.saving}
                  >
                    <SelectTrigger 
                      className="h-9 w-full bg-zinc-800 border-zinc-700 text-white font-mono text-sm rounded-none uppercase"
                      data-testid="admin-edit-state"
                    >
                      <SelectValue placeholder="SELECT STATE">
                        {editUserDialog.form.state ? `${editUserDialog.form.state.toUpperCase()} - ${US_STATES.find(s => s.code === editUserDialog.form.state?.toUpperCase())?.name.toUpperCase() || ''}` : "SELECT STATE"}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-700 max-h-48 overflow-y-auto">
                      {US_STATES.map(state => (
                        <SelectItem 
                          key={state.code} 
                          value={state.code}
                          className="text-white font-mono text-sm hover:bg-zinc-800 py-2 uppercase"
                        >
                          {state.code} - {state.name.toUpperCase()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-4">
                  <label className="block font-mono text-xs text-zinc-500 mb-1">ZIP</label>
                  <Input
                    name="zip_code"
                    value={editUserDialog.form.zip_code || ''}
                    onChange={(e) => {
                      // Restrict to 5 digits only
                      const value = e.target.value.replace(/\D/g, '').slice(0, 5);
                      handleEditFormChange({ target: { name: 'zip_code', value } });
                    }}
                    disabled={editUserDialog.saving}
                    maxLength={5}
                    className="h-9 w-full bg-zinc-800 border-zinc-700 text-white font-mono text-sm rounded-none"
                    data-testid="admin-edit-zip"
                  />
                </div>
              </div>
              
              {/* Manual International Flag - always show */}
              <div className="flex items-center gap-3 p-3 bg-purple-500/10 border border-purple-500/30">
                <Checkbox
                  id="is_international"
                  checked={editUserDialog.form.is_international || false}
                  onCheckedChange={(checked) => {
                    setEditUserDialog(prev => ({
                      ...prev,
                      form: { ...prev.form, is_international: checked }
                    }));
                  }}
                  disabled={editUserDialog.saving}
                  className="border-purple-500 data-[state=checked]:bg-purple-500 data-[state=checked]:border-purple-500"
                  data-testid="admin-is-international"
                />
                <div>
                  <label 
                    htmlFor="is_international" 
                    className="font-mono text-sm text-purple-400 cursor-pointer"
                  >
                    INTERNATIONAL CUSTOMER
                  </label>
                  <p className="font-mono text-[10px] text-zinc-500">
                    Flag this user as international (using US forwarding address)
                  </p>
                </div>
              </div>
              
              {/* Forwarding Verified toggle - only show for international users */}
              {(editUserDialog.form.is_international || (editUserDialog.form.country && editUserDialog.form.country !== 'US')) && (
                <div className="flex items-center gap-3 p-3 bg-zinc-800 border border-zinc-700">
                  <Checkbox
                    id="forwarding_verified"
                    checked={editUserDialog.form.forwarding_verified || false}
                    onCheckedChange={(checked) => {
                      setEditUserDialog(prev => ({
                        ...prev,
                        form: { ...prev.form, forwarding_verified: checked }
                      }));
                    }}
                    disabled={editUserDialog.saving}
                    className="border-zinc-500 data-[state=checked]:bg-tiffany-500 data-[state=checked]:border-tiffany-500"
                    data-testid="admin-forwarding-verified"
                  />
                  <div>
                    <label 
                      htmlFor="forwarding_verified" 
                      className="font-mono text-sm text-zinc-300 cursor-pointer"
                    >
                      FORWARDING ADDRESS VERIFIED
                    </label>
                    <p className="font-mono text-[10px] text-zinc-500">
                      Check this to allow checkout without suite/unit validation
                    </p>
                  </div>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button
                variant="outline"
                onClick={handleCloseEditDialog}
                disabled={editUserDialog.saving}
                className="rounded-none border-zinc-700 text-zinc-400"
              >
                CANCEL
              </Button>
              <Button
                onClick={handleSaveUserEdit}
                disabled={editUserDialog.saving}
                className="rounded-none bg-tiffany-500 text-black hover:bg-tiffany-400"
                data-testid="admin-save-user-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {editUserDialog.saving ? 'SAVING...' : 'SAVE CHANGES'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Flag User Dialog */}
        <Dialog 
          open={flagDialog.open} 
          onOpenChange={(open) => !flagDialog.saving && setFlagDialog({ open: false, user: null, reason: '', saving: false })}
        >
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white font-sans flex items-center gap-2">
                <Flag className="w-5 h-5 text-red-500" />
                FLAG USER
              </DialogTitle>
              <DialogDescription className="text-zinc-400 font-mono text-sm">
                {flagDialog.user?.email}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div>
                <label className="block font-mono text-xs text-zinc-500 mb-2">REASON (OPTIONAL)</label>
                <Input
                  value={flagDialog.reason}
                  onChange={(e) => setFlagDialog(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="E.G. SUSPICIOUS ACTIVITY, INVALID INFO..."
                  className="h-10 bg-zinc-800 border-zinc-700 text-white font-mono text-sm rounded-none uppercase placeholder:normal-case placeholder:text-zinc-600"
                  disabled={flagDialog.saving}
                />
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-orange-500/10 border border-orange-500/30">
                <input
                  type="checkbox"
                  id="require-admin-approval"
                  checked={flagDialog.requireAdminApproval || false}
                  onChange={(e) => setFlagDialog(prev => ({ ...prev, requireAdminApproval: e.target.checked }))}
                  className="mt-1 w-4 h-4 accent-orange-500"
                  disabled={flagDialog.saving}
                />
                <div>
                  <label htmlFor="require-admin-approval" className="font-mono text-sm text-orange-400 cursor-pointer">
                    REQUIRE ADMIN APPROVAL
                  </label>
                  <p className="font-mono text-[10px] text-zinc-500 mt-1">
                    User cannot self-unflag. Only an admin can remove this flag.
                  </p>
                </div>
              </div>
              
              <p className="font-mono text-[10px] text-zinc-500">
                {flagDialog.requireAdminApproval 
                  ? "User will be blocked until YOU manually approve their profile."
                  : "User will be blocked until they update their profile (then needs your review)."}
              </p>
            </div>
            <DialogFooter className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setFlagDialog({ open: false, user: null, reason: '', requireAdminApproval: false, saving: false })}
                disabled={flagDialog.saving}
                className="rounded-none border-zinc-700 text-zinc-400 hover:bg-zinc-800"
              >
                CANCEL
              </Button>
              <Button
                onClick={handleFlagUser}
                disabled={flagDialog.saving}
                className="rounded-none bg-red-600 text-white hover:bg-red-500"
              >
                {flagDialog.saving ? 'FLAGGING...' : 'FLAG USER'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
